import { useCallback, useEffect, useRef, useState } from 'react'
import { getTask } from 'actions/collaborativeTasks'

/**
 * Loads a single task with its timeline. Used by the right-side drawer.
 */
export const useTask = (taskId) => {
    const [task, setTask] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    // Tracks the most recently requested taskId so stale responses are ignored.
    const latestIdRef = useRef(taskId)

    const fetchData = useCallback(async () => {
        // Clear any previously-loaded task so the drawer doesn't briefly render
        // stale details when opened for a different id (or with no id).
        setTask(null)
        if (!taskId) {
            setLoading(false)
            setError(null)
            return
        }
        setLoading(true)
        setError(null)
        // Guard against out-of-order responses: only the latest taskId's result wins.
        const requestedId = taskId
        try {
            const res = await getTask(requestedId)()
            if (requestedId !== latestIdRef.current) return
            setTask(res?.response || res || null)
        } catch (e) {
            if (requestedId !== latestIdRef.current) return
            setError(e)
            setTask(null)
        } finally {
            if (requestedId === latestIdRef.current) setLoading(false)
        }
    }, [taskId])

    useEffect(() => {
        latestIdRef.current = taskId
        fetchData()
    }, [taskId, fetchData])

    return { task, loading, error, refetch: fetchData }
}
